/**
 * CurrencyOversight — the finance view of money that is not yet settled or
 * not yet valued.
 *
 * Three things that used to be invisible from here:
 *  · contributions held because no authorised rate exists, across every
 *    assembly — previously only the assembly that took the money could see it;
 *  · who set each exchange rate, when, and what it was before;
 *  · income that has not been tied back to the bank, with the provider's fee,
 *    so the ledger stops showing more money than ever arrived.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, History, Landmark, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import api, { getErrorMessage } from '@/lib/api';
import ExchangeRateRequests from './ExchangeRateRequests';

const money = (n, ccy) => `${ccy} ${Number(n || 0).toLocaleString(undefined,
  { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Held = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['fx-held'],
    queryFn: () => api.get('/fx/held-contributions').then((r) => r.data),
    retry: false,
  });
  if (isLoading) {
    return <p className="flex items-center gap-2 p-3 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" />Looking for held money…</p>;
  }
  const held = data?.held || [];
  return (
    <Card data-testid="held-contributions">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          Recorded but not yet valued
        </CardTitle>
        <CardDescription>
          Money given in a currency with no authorised rate. It is receipted and
          safe, and counted in its own currency only, until a rate is approved.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {Object.keys(data?.by_currency || {}).length ? (
          <div className="flex flex-wrap gap-2" data-testid="held-totals">
            {Object.entries(data.by_currency).map(([ccy, t]) => (
              <Badge key={ccy} variant="outline" className="border-amber-300 text-amber-800">
                {money(t.amount, ccy)} · {t.count} payment{t.count === 1 ? '' : 's'}
              </Badge>
            ))}
          </div>
        ) : null}
        {held.length ? held.map((h) => (
          <div key={h.payment_id}
               className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 px-3 py-2"
               data-testid={`held-${h.payment_id}`}>
            <span className="min-w-[10rem] flex-1 text-sm">
              <strong>{money(h.amount, h.currency)}</strong>
              <span className="block text-xs text-slate-500">
                {h.giver} · {h.assembly}{h.campaign_title ? ` · ${h.campaign_title}` : ''}
              </span>
            </span>
            <span className="text-xs text-slate-500">
              needs {h.currency} → {h.needs_rate_to} · {h.recorded_at}
              {h.receipt_number ? ` · ${h.receipt_number}` : ''}
            </span>
          </div>
        )) : (
          <p className="p-2 text-sm text-slate-500" data-testid="held-empty">
            Nothing is waiting on a rate.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const RateHistory = () => {
  const { data } = useQuery({
    queryKey: ['fx-rate-history'],
    queryFn: () => api.get('/fx/rate-history', { params: { limit: 50 } }).then((r) => r.data),
    retry: false,
  });
  const rows = data?.history || [];
  return (
    <Card data-testid="rate-history">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <History className="h-4 w-4 text-slate-500" />Exchange rate changes
        </CardTitle>
        <CardDescription>Who set each rate, when, and what it was before.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {rows.length ? rows.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-3 border-b border-slate-100 pb-1.5 text-sm"
               data-testid={`rate-change-${r.id}`}>
            <span className="w-28 font-medium">{r.from_ccy} → {r.to_ccy}</span>
            <span className="w-40">
              {r.old_rate ? <span className="text-slate-400 line-through">{r.old_rate}</span> : null}
              {' '}<strong>{r.new_rate}</strong>
            </span>
            <span className="min-w-[10rem] flex-1 text-xs text-slate-500">
              {r.rate_source}{r.reason ? ` — ${r.reason}` : ''}
            </span>
            <span className="text-xs text-slate-400">
              {r.changed_by || 'system'} · {(r.changed_at || '').slice(0, 10)}
            </span>
          </div>
        )) : (
          <p className="p-2 text-sm text-slate-500" data-testid="rate-history-empty">
            No rate has been set by hand yet.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const Settlements = () => {
  const qc = useQueryClient();
  const [openFor, setOpenFor] = useState(null);
  const [form, setForm] = useState({ fee: '', reference: '' });

  const { data } = useQuery({
    queryKey: ['unsettled-income'],
    queryFn: () => api.get('/finance/settlements/unsettled', { params: { limit: 25 } })
      .then((r) => r.data),
    retry: false,
  });

  const save = useMutation({
    mutationFn: (row) => api.post('/finance/settlements', {
      source: row.source, source_id: row.source_id,
      gross: row.amount, fee: Number(form.fee),
      net: Number((row.amount - Number(form.fee)).toFixed(2)),
      settlement_currency: row.currency, provider: row.method,
      provider_reference: form.reference,
    }),
    onSuccess: () => {
      toast.success('Settlement recorded — the fee is now in the ledger');
      setOpenFor(null);
      setForm({ fee: '', reference: '' });
      qc.invalidateQueries({ queryKey: ['unsettled-income'] });
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not record that settlement')),
  });

  const rows = data?.unsettled || [];
  return (
    <Card data-testid="unsettled-income">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Landmark className="h-4 w-4 text-slate-500" />Not yet tied to the bank
        </CardTitle>
        <CardDescription>
          Card and transfer income with no settled figure recorded. Enter the fee
          from the statement and the ledger will match what the bank credited.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {rows.length ? rows.map((r) => (
          <div key={r.source_id} className="rounded-lg border border-slate-200 px-3 py-2"
               data-testid={`unsettled-${r.source_id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <span className="min-w-[10rem] flex-1 text-sm">
                <strong>{money(r.amount, r.currency)}</strong>
                <span className="block text-xs text-slate-500">
                  {r.category} · {r.method} · {r.date}
                </span>
              </span>
              <Button size="sm" variant="outline"
                      onClick={() => setOpenFor(openFor === r.source_id ? null : r.source_id)}
                      data-testid={`settle-open-${r.source_id}`}>
                Record settlement
              </Button>
            </div>
            {openFor === r.source_id ? (
              <div className="mt-2 flex flex-wrap items-end gap-2 border-t border-slate-100 pt-2">
                <span>
                  <label className="block text-xs text-slate-500">Provider fee</label>
                  <Input type="number" step="0.01" value={form.fee} className="h-8 w-28"
                         data-testid="settle-fee"
                         onChange={(e) => setForm((f) => ({ ...f, fee: e.target.value }))} />
                </span>
                <span>
                  <label className="block text-xs text-slate-500">Statement reference</label>
                  <Input value={form.reference} className="h-8 w-48" data-testid="settle-ref"
                         onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} />
                </span>
                <span className="text-xs text-slate-500">
                  Net to bank:{' '}
                  <strong>{money(r.amount - Number(form.fee || 0), r.currency)}</strong>
                </span>
                <Button size="sm" onClick={() => save.mutate(r)}
                        disabled={form.fee === '' || Number(form.fee) < 0 || save.isPending}
                        data-testid="settle-save">
                  {save.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                  Save
                </Button>
              </div>
            ) : null}
          </div>
        )) : (
          <p className="p-2 text-sm text-slate-500" data-testid="unsettled-empty">
            Everything is tied back to the bank.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export const CurrencyOversight = () => (
  <div className="space-y-4" data-testid="currency-oversight">
    <ExchangeRateRequests />
    <Held />
    <Settlements />
    <RateHistory />
  </div>
);

export default CurrencyOversight;
